import { UploadPhotoTemplate } from '@templates';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../../routes/Routes';
import { useEffect, useState } from 'react';
import { emptyServiceLocationDetails, ErrorMessages } from '@types';
import { usePlanningBreachStore } from '../../../../hooks/usePlanningBreachStore';
import * as Sentry from '@sentry/react';
import { FileObject, useUploadImage, useDeleteImage } from '@hooks';
import {
  convertFilePartsToFileObjects,
  partitionFileObjects,
  findDeletedImage,
  PartitionFileObjectsType,
  uploadPhotoSchema,
  isCoordinatesInsideBorough,
} from '@utils';
import { PdfProvider } from '@shared/utils';
import { ACCEPTED_FILE_TYPES_WITH_PDF } from '@constants';

const UploadPhotoTextContent = () => {
  return (
    <div className="text-bodyLg my-4 flex flex-col gap-4">
      <p>
        Photos and documents help us locate and assess the issue.{' '}
        <a
          href="https://www.westminster.gov.uk/data-protection/report-it-image-disclaimer"
          className="text-action-link underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          Find out how we will be using your evidence.
        </a>
      </p>
      <p>
        You can upload up to 5 photos and documents. You can upload JPEG, PNG,
        GIF, PDF or WebP files.
      </p>
    </div>
  );
};
export const PhotoUpload = () => {
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState<FileObject[]>([]);
  const { data, get, update } = usePlanningBreachStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  //use mutateAsync due to async operations after successful upload
  const { mutateAsync: mutateImageUpload, isPending } = useUploadImage();
  const { mutate: mutateDelete } = useDeleteImage();
  const [processingPhoto, setProcessingPhoto] = useState(false);
  useEffect(() => {
    // retrieve previously uploaded images from localStorage and display them on page load
    const uploadedImages = (
      data.uploadedImages as unknown as { uploadedImages: string[] }
    ).uploadedImages;
    // @ts-ignore,
    const uploadedImagesFileName = data.uploadedImages.uploadedImagesFileName;
    // @ts-ignore,
    const uploadedImagesFileType = data.uploadedImages.uploadedImagesFileType;
    // @ts-ignore,
    const uploadedImagesBlob = data.uploadedImages.uploadedImagesBlob;
    convertFilePartsToFileObjects({
      uploadedImages,
      uploadedImagesFileName,
      uploadedImagesFileType,
      uploadedImagesBlob,
    }).then((images) => {
      setUploadedFiles(images);
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleFileChange = async (files: File[]) => {
    if (!files || files.length === 0) {
      return;
    }
    setProcessingPhoto(true);
    const newFile = {
      file: files[files.length - 1],
      blob_endpoint: '',
      blob_uri: '',
    };
    const uploadedFileObjects =
      uploadedFiles.length > 0 ? [...uploadedFiles, newFile] : [newFile];
    setUploadedFiles(uploadedFileObjects);
    try {
      uploadPhotoSchema.parse({ uploadedFiles: uploadedFileObjects });
      setUploadedFiles(uploadedFileObjects);
      const partitionedFiles: PartitionFileObjectsType =
        await partitionFileObjects(uploadedFileObjects);
      update('uploadedImages', partitionedFiles as unknown as string[]);
      if (partitionedFiles.geoDataArrays.length > 0) {
        const { latitude, longitude } = partitionedFiles.geoDataArrays[0];
        const isInside = await isCoordinatesInsideBorough([
          latitude as number,
          longitude as number,
        ]);
        if (isInside) {
          update('isPhotoLocationStored', true);
          update(
            'fileWithLoadedCoordinates',
            partitionedFiles.geoDataArrays[0].fileName as string,
          );
          update('serviceLocationDetailsFromFile', {
            ...emptyServiceLocationDetails,
            latitude,
            longitude,
          });
        }
      }
      setErrorMessages([]);
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessages([
        { path: 'upload-photo', message: (error as Error).message },
      ]);
    } finally {
      setProcessingPhoto(false);
    }
  };
  const handleDelete = (files: File[]) => {
    setProcessingPhoto(true);
    const deletedFileObject = findDeletedImage(uploadedFiles, files);
    let remainingFiles: FileObject[];
    if (deletedFileObject) {
      // if the photo has been uploaded to blob, delete it from the blob
      if (deletedFileObject.blob_endpoint) {
        remainingFiles = uploadedFiles.filter(
          (file) => file.blob_endpoint !== deletedFileObject.blob_endpoint,
        );
        try {
          mutateDelete(deletedFileObject.blob_endpoint);
          setUploadedFiles(remainingFiles);
          partitionFileObjects(remainingFiles).then((partitionedFiles) => {
            update('uploadedImages', partitionedFiles as unknown as string[]);
          });
          setErrorMessages([]);
        } catch (error) {
          Sentry.captureException(error);
          setErrorMessages([
            { path: 'upload-photo', message: (error as Error).message },
          ]);
        } finally {
          setProcessingPhoto(false);
        }
        // if not uploaded to the blob, delete from local storage
      } else {
        remainingFiles = uploadedFiles.filter(
          (file) => file.file.name !== deletedFileObject.file.name,
        );
        setUploadedFiles(remainingFiles);
        partitionFileObjects(remainingFiles).then((partitionedFiles) => {
          update('uploadedImages', partitionedFiles as unknown as string[]);
        });
        setErrorMessages([]);
      }
    } else {
      setErrorMessages([
        {
          path: 'upload-photo',
          message: 'Failed to delete image. Please try again',
        },
      ]);
    }
    setProcessingPhoto(false);
  };
  const handleUploadError = (error: string) => {
    if (error) {
      Sentry.captureException(error);
      setErrorMessages([{ path: 'upload-photo', message: error }]);
    }
  };
  const serviceLocationDetailsFromFile = get('serviceLocationDetailsFromFile');
  const { latitude, longitude } = serviceLocationDetailsFromFile ?? null;
  const buttonNext = {
    label: 'Nothing to upload',
    loading: isPending,
    disabled: processingPhoto,
    onClick: async () => {
      if (latitude && longitude) {
        update('isPhotoLocationStored', true);
        update('issueLocationData.latitude', latitude.toString());
        update('issueLocationData.longitude', longitude.toString());
      }
      if (uploadedFiles.length === 0) {
        navigate(RoutesList.ISSUE_LOCATION);
      }
      const uploadedImagesBlob = get('uploadedImagesBlob');
      let updatedImagesBlobsArray: typeof uploadedImagesBlob =
        uploadedImagesBlob.filter(
          (item: { blob_endpoint: string; blob_uri: string }) =>
            !!item.blob_endpoint && !!item.blob_uri,
        );
      for (const fileObject of uploadedFiles) {
        const { file, blob_endpoint, blob_uri } = fileObject;
        // if the file has not been uploaded to the blob, upload it
        if (!blob_endpoint || !blob_uri) {
          await mutateImageUpload(file, {
            onSuccess: (data) => {
              const updatedFileObject = {
                filename: file.name,
                blob_endpoint: data.blob_endpoint,
                blob_uri: data.blob_uri,
              };
              updatedImagesBlobsArray.push(updatedFileObject);
            },
            onError: (error) => {
              Sentry.captureException(error);
              setErrorMessages([
                { path: 'upload-photo', message: error.message },
              ]);
            },
          });
        }
      }
      update('uploadedImagesBlob', updatedImagesBlobsArray);
      navigate(RoutesList.ISSUE_LOCATION);
    },
  };
  const buttonPrevious = {
    disabled: processingPhoto || isPending,
    onClick: () => {
      navigate(RoutesList.ISSUE_TYPE);
    },
  };

  return (
    <PdfProvider isPdfAllowed={true}>
      <UploadPhotoTemplate
        formTitle="Upload evidence of the issue"
        dropzoneLabel="Upload evidence"
        description={<UploadPhotoTextContent />}
        handleFileChange={handleFileChange}
        handleUploadError={handleUploadError}
        handleDelete={handleDelete}
        buttonNext={buttonNext}
        buttonPrevious={buttonPrevious}
        maxNumberOfUpload={5}
        errorPath={'upload-photo'}
        errorMessages={errorMessages}
        initialFiles={uploadedFiles.map((file) => file.file)}
        acceptedFileTypes={ACCEPTED_FILE_TYPES_WITH_PDF}
      />
    </PdfProvider>
  );
};
